/**
 * Employment Type Utilities
 * 
 * Manages employment classifications and their associated permissions.
 * This separates role functionality from employment status.
 */

import { User, EmploymentType } from '../../types';

// ============================================================================
// EMPLOYMENT TYPE CONFIGURATIONS
// ============================================================================

export interface EmploymentTypeConfig {
  type: EmploymentType;
  name: string;
  description: string;
  accessLevel: number; // Higher = more access
  features: EmploymentFeature[];
}

export type EmploymentFeature = 
  | 'internal_systems'        // Access to internal tools and systems
  | 'internal_messaging'     // Internal company communication
  | 'full_analytics'         // Complete analytics access
  | 'document_management'    // Full document system access
  | 'project_creation'       // Can create new projects
  | 'team_management'        // Can manage internal teams
  | 'client_communication'   // Can communicate with clients
  | 'limited_access'         // Restricted to assigned projects only
  | 'guest_features';        // Basic guest-level features

/**
 * Employment type hierarchy and feature mapping
 */
export const EMPLOYMENT_TYPES: Record<EmploymentType, EmploymentTypeConfig> = {
  internal: {
    type: 'internal',
    name: 'Internal Employee',
    description: 'Full-time employees with complete system access and benefits',
    accessLevel: 100,
    features: [
      'internal_systems',
      'internal_messaging',
      'full_analytics',
      'document_management',
      'project_creation',
      'team_management',
      'client_communication'
    ]
  },
  contractor: {
    type: 'contractor',
    name: 'Contractor',
    description: 'Temporary workers with project-based access and limited system integration',
    accessLevel: 60,
    features: [
      'internal_messaging',
      'document_management',
      'project_creation',
      'client_communication'
    ]
  },
  external: {
    type: 'external',
    name: 'External User',
    description: 'Clients, partners, and vendors with restricted access to shared resources',
    accessLevel: 30,
    features: [
      'limited_access',
      'client_communication',
      'guest_features'
    ]
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Gets the employment type from a user object
 */
export function getEmploymentType(user: Partial<User> | null | undefined): EmploymentType {
  if (!user || !user.employmentType) {
    // Fallback classification when the explicit employmentType is absent.
    // External standing is derived from EVIDENCE — the user's scoped
    // relationships — not from any legacy account-role string. Company-side
    // representative relationships indicate an external counterparty.
    const EXTERNAL_RELATIONSHIP_TYPES: readonly string[] = [
      'vendor_representative',
      'partner_representative',
      'investor_representative',
      'company_representative',
      'reporting_recipient',
    ];
    const hasExternalRelationship = (user?.scopedRelationships ?? []).some(
      rel => EXTERNAL_RELATIONSHIP_TYPES.includes(rel.type),
    );
    if (hasExternalRelationship) {
      return 'external';
    }
    return 'internal'; // Default for internal accounts
  }

  return user.employmentType;
}

/**
 * Checks if a user has a specific employment feature
 */
export function hasEmploymentFeature(
  user: Partial<User> | null | undefined, 
  feature: EmploymentFeature
): boolean {
  const empType = getEmploymentType(user);
  const config = EMPLOYMENT_TYPES[empType];
  return config.features.includes(feature);
}

/**
 * Checks if a user is an internal employee (including contractors)
 */
export function isInternalUser(user: Partial<User> | null | undefined): boolean {
  const empType = getEmploymentType(user);
  return empType === 'internal' || empType === 'contractor';
}

/**
 * Checks if a user is external (client, partner, vendor)
 */
export function isExternalUser(user: Partial<User> | null | undefined): boolean {
  const empType = getEmploymentType(user);
  return empType === 'external';
}

/**
 * Checks if user can access internal systems (replaces old internal_team_member logic)
 */
export function canAccessInternalSystems(user: Partial<User> | null | undefined): boolean {
  return hasEmploymentFeature(user, 'internal_systems');
}

/**
 * Checks if user can manage teams (replaces role-based team management)
 */
export function canManageTeams(user: Partial<User> | null | undefined): boolean {
  return hasEmploymentFeature(user, 'team_management');
}

/**
 * Gets employment type display name
 */
export function getEmploymentTypeName(empType: EmploymentType): string {
  return EMPLOYMENT_TYPES[empType].name;
}

/**
 * Gets employment type description
 */
export function getEmploymentTypeDescription(empType: EmploymentType): string {
  return EMPLOYMENT_TYPES[empType].description;
}

/**
 * Gets all employment features for a user
 */
export function getUserEmploymentFeatures(user: Partial<User> | null | undefined): EmploymentFeature[] {
  const empType = getEmploymentType(user);
  return [...EMPLOYMENT_TYPES[empType].features];
}

/**
 * Compares employment access levels
 */
export function compareEmploymentAccess(typeA: EmploymentType, typeB: EmploymentType): number {
  const levelA = EMPLOYMENT_TYPES[typeA].accessLevel;
  const levelB = EMPLOYMENT_TYPES[typeB].accessLevel;
  return levelA - levelB;
}

/**
 * Validates if an employment type is valid
 */
export function isValidEmploymentType(type: string): type is EmploymentType {
  return Object.keys(EMPLOYMENT_TYPES).includes(type as EmploymentType);
}

/**
 * Gets all available employment types
 */
export function getAllEmploymentTypes(): EmploymentType[] {
  return Object.keys(EMPLOYMENT_TYPES) as EmploymentType[];
}

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Default employment type for new users
 */
export const DEFAULT_EMPLOYMENT_TYPE: EmploymentType = 'internal';

/**
 * Employment types that have internal access
 */
export const INTERNAL_EMPLOYMENT_TYPES: EmploymentType[] = ['internal', 'contractor'];

/**
 * Employment types that are external
 */
export const EXTERNAL_EMPLOYMENT_TYPES: EmploymentType[] = ['external'];

// ============================================================================
// WORKFORCE DISPLAY HELPER
// ============================================================================

/**
 * Determines if employmentType should be displayed/used for a given role context.
 * Employment classification is metadata, not authorization. Show it only for
 * delivery roles where the distinction is useful to workspace operators.
 */
export function shouldUseEmploymentType(
  roleOrUser?: Partial<User> | string | null
): boolean {
  if (!roleOrUser) return false;

  const TOKEN_DELIVERY = 'project_manager';
  const TOKEN_SUPPLIER = 'vendor';
  const WORKFORCE_ROLE_KEYS = new Set([TOKEN_DELIVERY, TOKEN_SUPPLIER]);

  if (typeof roleOrUser === 'string') {
    return WORKFORCE_ROLE_KEYS.has(roleOrUser);
  }

  const user = roleOrUser as Partial<User>;
  const workspaceRole = user.workspace_role?.toString();
  const displayRole = user.role?.toString();
  return WORKFORCE_ROLE_KEYS.has(workspaceRole ?? displayRole ?? '');
}
